import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from './services/employee.service';
import { ToastService } from 'src/app/services/toast.service';
import { SharedService } from 'src/app/services/shared.service';
import { Employee } from './services/employee.modal';
import { PhonenumberPipe } from 'src/app/pipes/phonenumber.pipe';
import { ValidatorsService } from 'src/app/services/validators.service';
import { GetItemsParams } from '../attorney/service/attorney.model';
import { Page, MaxLength, ToastType } from 'src/app/enums/enums';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { DialogueService } from 'src/app/services/dialogue.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss']
})
export class EmployeeComponent implements OnInit, OnDestroy {
  private EditEmployeeID: number | null = null;
  private Unsubscribe = new Subject<void>();
  private SearchSubject = new Subject<string>();
  public IsLoading = false;
  public ShowPassword = false;
  public EmployeeList: Employee[] = [];
  public IsEditEmployee = false;
  public TotalEmployee = 0;
  public MaxPhoneLength = MaxLength.Phone;
  public MaxZipLength = MaxLength.Zip;
  public PaginationParams!: GetItemsParams;
  public SearchVal: string = '';
  public AddEditEmplDialogue!: NgbModalRef;

  constructor(
    public apiService: ApiService,
    public sharedService: SharedService,
    private validatorService: ValidatorsService,
    private emplService: EmployeeService,
    private toast: ToastService,
    private phoneNumberPipe: PhonenumberPipe,
    private dialogue: DialogueService
  ) {
    this.PaginationParams = {
      searchValue: '',
      skip: 0,
      take: Number(this.sharedService.getData('records')) || Page.Size,
      sortColumn: 'firstName',
      clinicId: this.sharedService.CurrentClinic.value || 0,
      sortOrder: 'asc'
    };
  }

  EmplForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    title: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(16),
      this.validatorService.uppercaseValidator(),
      this.validatorService.lowercaseValidator(),
      this.validatorService.numberValidator()
    ]),
    phoneNumber: new FormControl('', [Validators.required]),
    clinicId: new FormControl(0),
    roleId: new FormControl(0, [Validators.required])
  });

  ngOnInit(): void {
    this.sharedService.CurrentClinic.pipe(takeUntil(this.Unsubscribe)).subscribe((val) => {
      this.PaginationParams.clinicId = Number(val);
      this.GetEmployeeList(this.PaginationParams);
    });

    this.SearchSubject.pipe(
      debounceTime(500), // Wait for 300ms pause in input
      distinctUntilChanged(), // Ignore if the search term is the same as previous
      switchMap((term) => {
        this.PaginationParams.searchValue = term;
        this.GetEmployeeList(this.PaginationParams);
        return '';
      })
    ).subscribe((results) => {
      console.log('results', results);
    });

    this.EmplForm.get('roleId')
      ?.valueChanges.pipe(takeUntil(this.Unsubscribe))
      .subscribe((roleId) => {
        const clinicId = this.EmplForm.get('clinicId');

        if (Number(roleId) !== 1) {
          clinicId?.enable();
          clinicId?.setValidators([Validators.required]);
        } else {
          clinicId?.disable();
          clinicId?.setValue(0);
          clinicId?.clearValidators();
        }
        clinicId?.updateValueAndValidity();
      });
  }

  ngOnDestroy(): void {
    this.Unsubscribe.next();
    this.Unsubscribe.complete();
  }

  OpenAddPopup() {
    const passwordControl = this.EmplForm.get('password');
    const emailControl = this.EmplForm.get('email');
    const clinicControl = this.EmplForm.get('clinicId');
    this.IsEditEmployee = false;
    this.EditEmployeeID = null;
    this.EmplForm.reset();
    this.ShowPassword = false;
    passwordControl?.enable();
    emailControl?.enable();
    clinicControl?.setValue(this.PaginationParams.clinicId);
    passwordControl?.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(16),
      this.validatorService.uppercaseValidator(),
      this.validatorService.lowercaseValidator(),
      this.validatorService.numberValidator()
    ]);
  }

  OpenEditPopup(id: number) {
    this.IsEditEmployee = true;
    this.ShowPassword = false;
    const res = this.emplService.GetEmployee(id);
    const passwordControl = this.EmplForm.get('password');
    const emailControl = this.EmplForm.get('email');

    passwordControl?.clearValidators();
    passwordControl?.disable();
    emailControl?.disable();

    res.subscribe({
      next: (res: any) => {
        const { id, userId, isDeleted, ...rest } = res.result;
        rest.password = '******';
        this.EditEmployeeID = id;
        this.EmplForm.setValue(rest);
      },
      complete: () => {}
    });
  }

  OpenPopup(model: any, isEdit?: boolean, id?: number) {
    if (isEdit && id) {
      this.OpenEditPopup(id);
    } else {
      this.OpenAddPopup();
    }
    this.AddEditEmplDialogue = this.dialogue.Open(model, { size: 'lg' });
  }

  AddEmployee() {
    /* Modify Values to Number */
    this.EmplForm.value.roleId = Number(this.EmplForm.value.roleId);
    this.EmplForm.value.clinicId = this.EmplForm.value.roleId === 1 ? 0 : Number(this.EmplForm.value.clinicId);
    this.EmplForm.value.phoneNumber = this.phoneNumberPipe.transform(this.EmplForm.value.phoneNumber, 'unformat');

    const response = this.emplService.AddEmployee(this.EmplForm.value);
    this.IsLoading = true;
    response.subscribe({
      next: (res) => {
        this.toast.showToast({
          message: res.message,
          type: ToastType.Success
        });
        this.EmplForm.reset();
      },
      error: (err) => {
        this.IsLoading = false;
      },
      complete: () => {
        this.EmplForm.reset();
        this.GetEmployeeList(this.PaginationParams);
      }
    });
  }

  UpdateEmployee() {
    if (this.EmplForm.valid) {
      this.EmplForm.value.roleId = Number(this.EmplForm.value.roleId);
      this.EmplForm.value.clinicId = Number(this.EmplForm.value.clinicId) || 0;
      this.EmplForm.value.phoneNumber = this.phoneNumberPipe.transform(this.EmplForm.value.phoneNumber, 'unformat');
      const requestBody = { userId: this.EditEmployeeID, ...this.EmplForm.value };
      const response = this.emplService.UpdateEmployee(requestBody);
      this.IsLoading = true;
      response.subscribe({
        next: (res) => {
          this.toast.showToast({
            message: res.message,
            type: ToastType.Success
          });
          this.EmplForm.reset();
          this.EditEmployeeID = null;
        },
        error: (err) => {
          this.IsLoading = false;
        },
        complete: () => {
          this.GetEmployeeList(this.PaginationParams);
        }
      });
    } else {
      console.log('Form is invalid');
    }
  }

  GetEmployeeList(params: GetItemsParams) {
    this.IsLoading = true;
    this.emplService.GetEmployees(params).subscribe({
      next: (res) => {
        this.EmployeeList = [...res.result.data];
        this.EmployeeList.map((emp) => {
          emp.clinicName = this.apiService.GetClinicName(emp.clinicId);
        });
        this.TotalEmployee = res.result.count;
      },
      error: (err) => {
        this.IsLoading = false;
      },
      complete: () => (this.IsLoading = false)
    });
  }

  DeleteEmployee(Emp: string) {
    const getConfirmation = this.dialogue.GetConfirmation('Delete Employee ?', 'Do you want to delete ' + Emp + ' ?', {
      actionName: 'Delete',
      actionClass: 'btn-danger',
      actionIcon: 'bi-exclamation-octagon'
    });

    getConfirmation.result.then((res) => {
      if (res) {
        if (this.EditEmployeeID) {
          this.IsLoading = true;
          this.emplService.DeleteEmployee(this.EditEmployeeID).subscribe({
            next: (res) => {
              this.toast.showToast({ message: res.message, type: ToastType.Success });
              this.EmplForm.reset();
              this.AddEditEmplDialogue.close();
            },
            error: (err) => {
              this.IsLoading = false;
            },
            complete: () => {
              this.GetEmployeeList(this.PaginationParams);
            }
          });
        }
      }
    });
  }

  SortData(sortBy: string) {
    this.PaginationParams.sortColumn = sortBy;
    this.PaginationParams.sortOrder = this.PaginationParams.sortOrder === 'asc' ? 'desc' : 'asc';
    this.GetEmployeeList(this.PaginationParams);
  }

  SearchData() {
    this.SearchSubject.next(this.SearchVal);
  }

  /* Pagination */
  onPageChange(newPage: number): void {
    this.PaginationParams.skip = (newPage - 1) * this.PaginationParams.take;
    this.GetEmployeeList(this.PaginationParams);
  }
  onPageSizeChange(newPageSize: number): void {
    this.PaginationParams.take = newPageSize;
    this.PaginationParams.skip = 0;
    this.sharedService.setData('records', this.PaginationParams.take);
    this.GetEmployeeList(this.PaginationParams);
  }
}
